import React, { useCallback, useRef, useState, useEffect } from 'react';
import { useTheme, getColor, getShadow, getBorderRadius, spacing } from '../../theme/useTheme';
import { getData } from '../../services/api';
import { getAccountStats, awardInspirationPoints } from '../../api/auth';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Image,
  RefreshControl,
  Animated,
  Platform,
  Share,
  StyleSheet,
  Dimensions,
  ImageBackground,
  Linking,
  AppState,
  Alert
} from 'react-native';
import * as Notifications from 'expo-notifications';
import Constants from 'expo-constants';
import * as NetInfo from '@react-native-community/netinfo';
import { StatusBar } from 'expo-status-bar';
import { Ionicons } from '@expo/vector-icons';
import { AuthContext } from '../../contexts/AuthContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { useNotifications } from '../../contexts/NotificationContext';
import { H1, H2, Body1, Body2, Caption, ButtonText } from '../../components/common/TextStyles';
import VideoModal from '../../components/common/VideoModal';
import Skeleton from '../../components/common/Skeleton';
import MentorProfileModal from '../../components/mentor/MentorProfileModal';
import { getImageUrl } from '../../utils/imageUtils';
import HoneyPotAPI from '../../services/honeypot';

// Event Components
import EventPassbook from '../../components/events/EventPassbook';
import EventRadar from '../../components/events/EventRadar';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

const Dashboard = () => {
  const { user, isNewRegistration, clearNewRegistration } = React.useContext(AuthContext);
  const { unreadCount } = useNotifications();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const [refreshing, setRefreshing] = useState(false);
  const [inspiration, setInspiration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inspirationLoading, setInspirationLoading] = useState(false);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [mentorModalVisible, setMentorModalVisible] = useState(false);
  const appStateRef = useRef(AppState.currentState);

  const [userStats, setUserStats] = useState({
    points: 0,
    level: 1,
    courseCount: 0,
    progress: 0,
    nextMilestone: 'Youth Explorer'
  });

  const [accountStats, setAccountStats] = useState(null);
  const [spotlightBusiness, setSpotlightBusiness] = useState(null);
  const [mentors, setMentors] = useState([]);
  const [mentorsLoading, setMentorsLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [accountLoading, setAccountLoading] = useState(true);
  const [marketplaceLoading, setMarketplaceLoading] = useState(true);

  const [activeHoneyPot, setActiveHoneyPot] = useState(null);
  const honeyPotAnim = useRef(new Animated.Value(1)).current;

  const scrollY = useRef(new Animated.Value(0)).current;

  const fetchUserStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const data = await getData('/user/dashboard-summary');
      if (data) setUserStats(data);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchAccountStats = useCallback(async () => {
    try {
      setAccountLoading(true);
      const data = await getAccountStats();
      if (data) setAccountStats(data);
    } catch (error) {
      console.error('Error fetching account stats:', error);
    } finally {
      setAccountLoading(false);
    }
  }, []);

  const fetchMentors = useCallback(async () => {
    try {
      setMentorsLoading(true);
      const data = await getData('/user/mentors');
      if (data) setMentors(data);
    } catch (error) {
      console.error('Error fetching mentors:', error);
    } finally {
      setMentorsLoading(false);
    }
  }, []);

  const fetchMarketplaceData = useCallback(async () => {
    try {
      setMarketplaceLoading(true);
      const data = await getData('/marketplace/featured');
      if (data && data.length > 0) {
        setSpotlightBusiness(data[Math.floor(Math.random() * data.length)]);
      }
    } catch (error) {
      console.error('Error fetching marketplace data:', error);
    } finally {
      setMarketplaceLoading(false);
    }
  }, []);

  const fetchInspiration = useCallback(async (showSpinner = false) => {
    try {
      if (showSpinner) setInspirationLoading(true);
      const data = await getData('/inspirations/today', 3);
      setInspiration(data);
    } catch (error) {
      console.error('Error fetching inspiration:', error);
    } finally {
      setLoading(false);
      setInspirationLoading(false);
    }
  }, []);

  const fetchActiveHoneyPot = useCallback(async () => {
    try {
      const challenge = await HoneyPotAPI.getActiveChallenge();
      setActiveHoneyPot(challenge);
      if (challenge) {
        Animated.loop(
          Animated.sequence([
            Animated.timing(honeyPotAnim, { toValue: 1.05, duration: 800, useNativeDriver: true }),
            Animated.timing(honeyPotAnim, { toValue: 1, duration: 800, useNativeDriver: true })
          ])
        ).start();
      }
    } catch (e) {
      console.warn('Failed to fetch Honey Pot challenge:', e);
    }
  }, []);

  // Re-fetch a fresh random inspiration whenever the app comes to foreground
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (
        appStateRef.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        fetchInspiration();
        fetchActiveHoneyPot();
      }
      appStateRef.current = nextAppState;
    });
    return () => subscription.remove();
  }, [fetchInspiration]);

  useEffect(() => {
    if (isNewRegistration) {
      clearNewRegistration();
      // Delay navigation slightly so the AppNavigator tab stack is fully mounted
      const timer = setTimeout(() => {
        navigation.navigate('Profile', {
          screen: 'EditProfile',
          params: { showWelcome: true }
        });
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [isNewRegistration]);

  useEffect(() => {
    fetchInspiration();
    fetchUserStats();
    fetchAccountStats();
    fetchMarketplaceData();
    fetchMentors();
    fetchActiveHoneyPot();
  }, [fetchInspiration, fetchUserStats, fetchAccountStats, fetchMarketplaceData, fetchMentors, fetchActiveHoneyPot]);

  // Request Notification Permissions on Dashboard mount
  useEffect(() => {
    if (!Constants.isDevice) return;

    // Add a slight delay to ensure UI is ready and transition is complete
    const timer = setTimeout(async () => {
      try {
        const { status: existingStatus } = await Notifications.getPermissionsAsync();
        let finalStatus = existingStatus;

        if (existingStatus !== 'granted') {
          const { status } = await Notifications.requestPermissionsAsync();
          finalStatus = status;
        }

        if (finalStatus !== 'granted') {
          Alert.alert(
            'Notifications Required',
            'Push notifications are required for you to receive important updates. Please enable them in your device settings.',
            [
              { text: 'Cancel', style: 'cancel' },
              { text: 'Open Settings', onPress: () => Linking.openSettings() }
            ]
          );
        }
      } catch (e) {
        console.warn('Dashboard notification permission request error:', e);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  const getYTId = (url) => {
    if (!url || typeof url !== 'string') return '';
    // Standard, mobile and Shorts URLs
    let match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|shorts\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    if (match) return match[1];
    return '';
  };

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    Promise.all([
      fetchInspiration(),
      fetchUserStats(),
      fetchAccountStats(),
      fetchMarketplaceData(),
      fetchMentors(),
      fetchActiveHoneyPot()
    ]).finally(() => {
      setRefreshing(false);
    });
  }, [fetchInspiration, fetchUserStats, fetchAccountStats, fetchMarketplaceData, fetchMentors]);

  const handleNavigation = (screen) => {
    navigation.navigate(screen);
  };

  const handleVideoPress = async (videoUrl) => {
    setCurrentVideoUrl(videoUrl);
    setVideoModalVisible(true);

    // Award point for interaction
    try {
      await awardInspirationPoints();
    } catch (err) {
      console.warn('[Dashboard] Points award failed:', err);
    }
  };

  const handleShare = async () => {
    try {
      const shareContent = inspiration?.verse?.content || "Therefore let us move beyond the elementary teachings about Christ and be taken forward to maturity...";
      const result = await Share.share({
        message: shareContent + "\n\nShared via Youth Connect App",
      });
    } catch (error) {
      console.error('Error sharing content:', error);
    }
  };

  const firstName = user?.name?.split(' ')[0] || 'Friend';

  // Badge config for progress card
  const BADGE_CFG = {
    Bronze: { emoji: '🥉', accent: '#F59E0B', grad: ['#78350F', '#D97706'] },
    Silver: { emoji: '🥈', accent: '#CBD5E1', grad: ['#374151', '#6B7280'] },
    Gold: { emoji: '🥇', accent: '#FCD34D', grad: ['#78350F', '#B45309'] },
    Platinum: { emoji: '💎', accent: '#A5B4FC', grad: ['#1E1B4B', '#4338CA'] },
  };
  const curBadge = accountStats?.badge || 'Bronze';
  const badgeCfg = BADGE_CFG[curBadge] || BADGE_CFG.Bronze;
  const xpPct = Math.min(accountStats?.progressPercentage || 0, 100);

  // Header Animation values — reduced for compact pill stats row
  const headerHeight = 260;

  const headerOpacity = scrollY.interpolate({
    inputRange: [0, 60],
    outputRange: [1, 0],
    extrapolate: 'clamp'
  });

  const stickyHeaderOpacity = scrollY.interpolate({
    inputRange: [80, 120],
    outputRange: [0, 1],
    extrapolate: 'clamp'
  });

  const stickyTranslateY = scrollY.interpolate({
    inputRange: [0, 100],
    outputRange: [-150, 0],
    extrapolate: 'clamp'
  });

  return (
    <View style={styles.container}>
      <StatusBar style="light" />

      {/* Background Aura Section (Fixed) */}
      <View style={[styles.headerBackground, { height: headerHeight + 50 }]}>
        <LinearGradient
          colors={['#7c3aed', '#6366f1', '#4f46e5']}
          style={StyleSheet.absoluteFill}
        />
        <View style={styles.auraContainer}>
          <View style={[styles.aura, styles.aura1]} />
          <View style={[styles.aura, styles.aura2]} />
          <View style={[styles.aura, styles.aura3]} />
        </View>
      </View>

      {/* Mini Sticky Header (Shows on scroll) */}
      <Animated.View
        style={[
          styles.miniStickyHeader,
          {
            paddingTop: insets.top + 5,
            opacity: stickyHeaderOpacity,
            transform: [{ translateY: stickyTranslateY }]
          }
        ]}
        pointerEvents="box-none"
      >
        <LinearGradient
          colors={['#6366f1', '#4f46e5']}
          style={StyleSheet.absoluteFill}
          pointerEvents="none"
        />
        <View style={styles.miniHeaderContent}>
          <H2 style={styles.miniHeaderTitle}>{firstName}'s Activity</H2>
          <View style={styles.miniStatsContainer}>
            <View style={styles.miniStatPill}>
              <Ionicons name="star" size={10} color="#FBBF24" />
              <Caption style={styles.miniStatText}>{userStats.points} pts</Caption>
            </View>
          </View>
        </View>
      </Animated.View>

      <Animated.ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContent}
        showsVerticalScrollIndicator={false}
        onScroll={Animated.event(
          [{ nativeEvent: { contentOffset: { y: scrollY } } }],
          { useNativeDriver: true }
        )}
        scrollEventThrottle={16}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor="#FFFFFF"
            progressViewOffset={insets.top + 20}
          />
        }
      >
        {/* Full Hero Header - Matching Image Styling */}
        <Animated.View style={[styles.heroHeader, { paddingTop: insets.top + 20, opacity: headerOpacity }]}>
          <View style={styles.topBar}>
            <TouchableOpacity onPress={() => handleNavigation('Profile')} style={styles.avatarWrapper}>
              <View style={styles.avatarBorder}>
                {user?.profileImageUrl ? (
                  <Image source={{ uri: getImageUrl(user.profileImageUrl) }} style={styles.avatarImage} />
                ) : (
                  <View style={styles.avatarPlaceholder}><Ionicons name="person" size={24} color="#6366F1" /></View>
                )}
              </View>
              <View style={styles.statusDot} />
            </TouchableOpacity>

            <View style={styles.greetingWrapper}>
              <Caption style={styles.greetLabel}>Hi there,</Caption>
              <H2 style={styles.greetName}>{firstName} 👋</H2>
            </View>

            <View style={styles.headerIcons}>
              <TouchableOpacity style={styles.iconCircle} onPress={() => handleNavigation('Wallet')}>
                <Ionicons name="wallet-outline" size={20} color="#FFFFFF" />
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.iconCircle}
                onPress={() => handleNavigation('Notifications')}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="notifications-outline" size={20} color="#FFFFFF" />
                {unreadCount > 0 && (
                  <View style={styles.notifBadge}>
                    <Text style={styles.notifBadgeText}>{unreadCount > 9 ? '9+' : unreadCount}</Text>
                  </View>
                )}
              </TouchableOpacity>
            </View>

          </View>

          {/* Stats Pill Row — compact, horizontal */}
          <View style={styles.statsPillRow}>
            {statsLoading ? (
              <View style={{ flexDirection: 'row', flex: 1, gap: 10, paddingHorizontal: 10 }}>
                <Skeleton width="30%" height={30} borderRadius={6} style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
                <Skeleton width="30%" height={30} borderRadius={6} style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
                <Skeleton width="30%" height={30} borderRadius={6} style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
              </View>
            ) : (
              <>
                <View style={styles.statPillCard}>
                  <Ionicons name="school-outline" size={13} color="rgba(255,255,255,0.7)" />
                  <Caption style={styles.statPillValue}>{userStats.courseCount}</Caption>
                  <Caption style={styles.statPillLabel}>Courses</Caption>
                </View>
                <View style={styles.statPillDivider} />
                <TouchableOpacity
                  style={styles.statPillCard}
                  onPress={() => handleNavigation('AccountProgress')}
                  activeOpacity={0.7}
                >
                  <Ionicons name="star-outline" size={13} color="#FBBF24" />
                  <Caption style={styles.statPillValue}>{accountStats?.points ?? userStats.points}</Caption>
                  <Caption style={styles.statPillLabel}>Points</Caption>
                </TouchableOpacity>
                <View style={styles.statPillDivider} />
                <View style={styles.statPillCard}>
                  <Text style={styles.statPillEmoji}>{badgeCfg.emoji}</Text>
                  <Caption style={styles.statPillValue}>Lv {accountStats?.level ?? userStats.level}</Caption>
                  <Caption style={styles.statPillLabel}>{curBadge}</Caption>
                </View>
              </>
            )}
          </View>
        </Animated.View>

        {/* Main Content Area (Reverted to earlier dashboard styling) */}
        <View style={styles.contentSurface}>

          {/* Daily Vibe — Redesigned for Functional Video Preview */}
          <View style={styles.vibeCardModern}>
            {/* Absolute fill layer for the tinted background to prevent Android shadow outline issues */}
            {(inspiration?.verse?.background_color || inspiration?.video?.background_color) && (
              <View
                style={[
                  StyleSheet.absoluteFill,
                  {
                    backgroundColor: (inspiration?.verse?.background_color || inspiration?.video?.background_color) + '15',
                    borderRadius: 14
                  }
                ]}
              />
            )}
            <View style={styles.vibeInner}>
              <View style={styles.vibeHeader}>
                <View style={styles.vibeBadge}>
                  <Ionicons name="flash" size={10} color="#6366F1" />
                  <Caption style={styles.badgeText}>Daily Inspiration</Caption>
                  {inspiration?.is_fallback && (
                    <Caption style={styles.fallbackBadge}>• From Archive</Caption>
                  )}
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                  <TouchableOpacity
                    onPress={() => fetchInspiration(true)}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    disabled={inspirationLoading}
                  >
                    <Ionicons
                      name={inspirationLoading ? 'hourglass-outline' : 'shuffle-outline'}
                      size={18}
                      color="#6366F1"
                    />
                  </TouchableOpacity>
                  <TouchableOpacity onPress={handleShare}>
                    <Ionicons name="share-outline" size={18} color="#6366F1" />
                  </TouchableOpacity>
                </View>
              </View>

              {inspirationLoading || !inspiration ? (
                <View style={{ gap: 10 }}>
                  <Skeleton width="100%" height={20} borderRadius={4} />
                  <Skeleton width="80%" height={20} borderRadius={4} />
                  <Skeleton width="100%" height={180} borderRadius={12} style={{ marginTop: 10 }} />
                </View>
              ) : (
                <>
                  <Body1 style={styles.vibeContent}>
                    {inspiration?.verse?.content || "Therefore let us move beyond the elementary teachings about Christ and be taken forward to maturity..."}
                  </Body1>

                  {inspiration?.video && (
                    <TouchableOpacity
                      style={styles.vibeVideoPreview}
                      onPress={() => handleVideoPress(inspiration.video.content)}
                      activeOpacity={0.9}
                    >
                      <ImageBackground
                        source={{ uri: `https://img.youtube.com/vi/${getYTId(inspiration.video.content)}/hqdefault.jpg` }}
                        style={styles.vibeThumb}
                        imageStyle={{ borderRadius: 10 }}
                      >
                        <LinearGradient
                          colors={['transparent', 'rgba(0,0,0,0.6)']}
                          style={[StyleSheet.absoluteFill, { borderRadius: 10 }]}
                        />
                        <View style={styles.vibePlayOverlay}>
                          <View style={styles.vibePlayCircle}>
                            <Ionicons name="play" size={24} color="#FFFFFF" />
                          </View>
                          <Caption style={styles.vibePlayTxt}>Watch Teaching</Caption>
                        </View>
                      </ImageBackground>
                    </TouchableOpacity>
                  )}

                  <View style={styles.vibeFooter}>
                    <View style={styles.vibeAuthor}>
                      <View style={styles.authorIcon} />
                      <Caption style={styles.authorName}>Youth Connect</Caption>
                    </View>
                    {!inspiration?.video && (
                      <TouchableOpacity style={styles.watchBtn} onPress={handleShare}>
                        <Ionicons name="heart" size={12} color="#6366F1" />
                        <Caption style={styles.watchText}>Be Inspired</Caption>
                      </TouchableOpacity>
                    )}
                  </View>
                </>
              )}
            </View>
          </View>

          {/* Account Progress Card — directly below Inspiration */}
          <TouchableOpacity
            style={styles.progressSection}
            onPress={() => handleNavigation('AccountProgress')}
            activeOpacity={0.9}
          >
            <LinearGradient colors={['#1e1b4b', '#312e81']} style={styles.progressCard}>
              {statsLoading ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, gap: 15, paddingHorizontal: 15 }}>
                  <Skeleton width={60} height={60} borderRadius={30} style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
                  <View style={{ flex: 1, gap: 8 }}>
                    <Skeleton width="70%" height={15} borderRadius={4} style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
                    <Skeleton width="90%" height={8} borderRadius={4} style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
                  </View>
                </View>
              ) : (
                <>
                  <View style={styles.progressTextSide}>
                    <Body1 style={styles.progressTitle}>Account Progress</Body1>
                    <Caption style={styles.progressLevel}>{userStats.progress}% to {userStats.nextMilestone}</Caption>
                    <View style={styles.progressTrack}>
                      <View style={[styles.progressFill, { width: `${userStats.progress}%` }]} />
                    </View>
                  </View>
                  <View style={styles.rocketCircle}>
                    <Ionicons name="rocket" size={24} color="#FBBF24" />
                  </View>
                </>
              )}
            </LinearGradient>
          </TouchableOpacity>
          {/* Mentors & Coaches */}
          <View style={styles.leadersSection}>
            <View style={styles.sectionHeaderRow}>
              <H2 style={styles.surfaceTitle}>Mentors & Coaches</H2>
              <TouchableOpacity
                style={styles.mentorActionBtn}
                onPress={() => handleNavigation('MentorHub')}
              >
                <Caption style={styles.mentorActionText}>Mentors Hub</Caption>
                <Ionicons name="chevron-forward" size={12} color="#6366F1" />
              </TouchableOpacity>
            </View>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.leadersScroll}>
              {mentorsLoading ? (
                <View style={{ flexDirection: 'row', gap: 15 }}>
                  {[1, 2, 3].map(i => (
                    <View key={i} style={{ width: 100, alignItems: 'center', gap: 8 }}>
                      <Skeleton width={64} height={64} borderRadius={32} />
                      <Skeleton width={60} height={12} borderRadius={4} />
                      <Skeleton width={40} height={8} borderRadius={4} />
                    </View>
                  ))}
                </View>
              ) : mentors.length > 0 ? (
                mentors.map((mentor, index) => {
                  const colors = ['#6366F1', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6'];
                  const color = colors[index % colors.length];
                  return (
                    <MentorCard
                      key={mentor.id || index}
                      name={mentor.name}
                      type={mentor.mentorType}
                      image={mentor.profileImageUrl}
                      color={color}
                      socialLinks={mentor.socialLinks}
                      onPress={() => {
                        setSelectedMentor(mentor);
                        setMentorModalVisible(true);
                      }}
                    />
                  );
                })
              ) : (
                <View style={styles.emptyLeaders}>
                  <Ionicons name="people-outline" size={24} color="#CBD5E1" />
                  <Caption style={styles.emptyLeadersTxt}>No active mentors found</Caption>
                </View>
              )}
            </ScrollView>
          </View>


          {/* Discovery Hub — compact 2x2 grid after Account Progress */}
          <View style={styles.hubSection}>
            <View style={styles.sectionHeaderRow}>
              <H2 style={styles.surfaceTitle}>Discovery Hub</H2>
              <Caption style={styles.hubSubtitle}>Explore all features</Caption>
            </View>
            <View style={styles.hubGridCompact}>
              <HubCard
                label="Learning"
                desc="Courses & skills"
                icon="school"
                color="#6366F1"
                onPress={() => handleNavigation('Soma')}
              />
              <HubCard
                label="Finances"
                desc="Budget & savings"
                icon="wallet"
                color="#10B981"
                onPress={() => handleNavigation('Pesa')}
              />
              <HubCard
                label="Market"
                desc="Buy & sell"
                icon="cart"
                color="#F59E0B"
                onPress={() => handleNavigation('Marketplace')}
              />
              <HubCard
                label="Community"
                desc="Events & social"
                icon="people"
                color="#EC4899"
                onPress={() => handleNavigation('EventsList')}
              />
            </View>
          </View>

          {/* Honey Pot Banner (Compact) */}
          {activeHoneyPot ? (
            <TouchableOpacity
              activeOpacity={0.9}
              onPress={() => navigation.navigate('HoneyPot')}
              style={{ marginHorizontal: 22, marginBottom: 25 }}
            >
              <LinearGradient colors={['#F59E0B', '#D97706']} style={{ borderRadius: 14, padding: 14, flexDirection: 'row', alignItems: 'center', shadowColor: '#D97706', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.2, shadowRadius: 5, elevation: 4 }}>
                <View style={{ width: 48, height: 48, borderRadius: 24, backgroundColor: 'white', justifyContent: 'center', alignItems: 'center', marginRight: 14, overflow: 'hidden' }}>
                  <Animated.Image
                    source={require('../../assets/badger_mascot_white.jpg')}
                    style={{ width: 38, height: 38, transform: [{ scale: honeyPotAnim }] }}
                    resizeMode="contain"
                  />
                </View>
                <View style={{ flex: 1, justifyContent: 'center' }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Body1 style={{ color: 'white', fontWeight: '700' }}>Mb's Honey Pot</Body1>
                    <Caption style={{ color: '#FEF3C7', fontWeight: 'bold' }}>
                      {activeHoneyPot.userAttempt ? 'COMPLETED ✨' : 'PLAY ✨'}
                    </Caption>
                  </View>
                  <Caption style={{ color: '#FEF3C7' }} numberOfLines={1}>Topic: {activeHoneyPot.title}</Caption>
                </View>
              </LinearGradient>
            </TouchableOpacity>
          ) : (
            <View style={{ marginHorizontal: 22, marginBottom: 25, backgroundColor: 'rgba(245, 158, 11, 0.08)', padding: 12, borderRadius: 12, flexDirection: 'row', alignItems: 'center' }}>
              <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(245, 158, 11, 0.15)', justifyContent: 'center', alignItems: 'center', marginRight: 12 }}>
                <Text style={{ fontSize: 16 }}>💤</Text>
              </View>
              <View style={{ flex: 1 }}>
                <Body2 style={{ color: '#B45309', fontWeight: '600' }}>Honey Pot is resting</Body2>
                <Caption style={{ color: '#D97706' }}>Check back later for sweet trivia.</Caption>
              </View>
            </View>
          )}

          {/* Events Feature: Passbook & Radar (Replaces static feed) */}
          <View style={styles.eventsSection}>
            <View style={[styles.sectionHeaderRow, { paddingHorizontal: 22 }]}>
              <H2 style={styles.surfaceTitle}>Events You've Joined</H2>
              <TouchableOpacity onPress={() => navigation.navigate('EventsList', { filter: 'joined' })}>
                <Caption style={styles.linkBlue}>View All</Caption>
              </TouchableOpacity>
            </View>
            <EventPassbook />

            <View style={[styles.sectionHeaderRow, { paddingHorizontal: 22, marginTop: 15 }]}>
              <H2 style={styles.surfaceTitle}>Upcoming Events</H2>
              <TouchableOpacity onPress={() => handleNavigation('EventsList')}>
                <Caption style={styles.linkBlue}>Explore</Caption>
              </TouchableOpacity>
            </View>
            <EventRadar />
          </View>

          {/* Marketplace Trends - Dynamic Navigation */}
          <View style={styles.trendsSection}>
            <H2 style={styles.surfaceTitle}>Marketplace Trends</H2>
            <View style={styles.trendsContainer}>
              <TrendChip name="Handmade" count="+12" color="#F59E0B" onPress={() => navigation.navigate('Marketplace', { category: 'Crafts' })} />
              <TrendChip name="Produce" count="+8" color="#10B981" onPress={() => navigation.navigate('Marketplace', { category: 'Agri' })} />
              <TrendChip name="Tech" count="+15" color="#6366F1" onPress={() => navigation.navigate('Marketplace', { category: 'Tech' })} />
            </View>
          </View>

          {/* Business Spotlight - Real Data */}
          <View style={styles.spotlightSection}>
            <View style={styles.sectionHeaderRow}>
              <H2 style={styles.surfaceTitle}>Recommended Business</H2>
            </View>
            <View style={styles.spotlightCard}>
              {marketplaceLoading ? (
                <View style={{ gap: 12 }}>
                  <Skeleton width="100%" height={180} borderRadius={14} />
                  <View style={{ padding: 15, gap: 8 }}>
                    <Skeleton width="60%" height={20} borderRadius={4} />
                    <Skeleton width="90%" height={12} borderRadius={4} />
                  </View>
                </View>
              ) : (
                <>
                  <Image
                    source={{ uri: getImageUrl(spotlightBusiness?.photos?.[0]?.url || spotlightBusiness?.logo_url) || 'https://images.unsplash.com/photo-1556740738-b6a63e27c4df?w=500' }}
                    style={styles.spotlightImage}
                  />
                  <View style={styles.spotlightOverlay}>
                    <Body1 style={styles.spotlightTitle}>{spotlightBusiness?.business_name || 'Discover Startups'}</Body1>
                    <Caption style={styles.spotlightText}>{spotlightBusiness?.description || 'Support innovative youth businesses on the marketplace.'}</Caption>
                    <TouchableOpacity
                      style={styles.spotlightBtn}
                      onPress={() => spotlightBusiness ? navigation.navigate('BusinessDetail', { listingId: spotlightBusiness.id }) : navigation.navigate('Marketplace')}
                    >
                      <Caption style={styles.spotlightBtnText}>VISIT STORE</Caption>
                    </TouchableOpacity>
                  </View>
                </>
              )}
            </View>
          </View>

          {/* Roadmap placeholders - Restored */}
          <View style={styles.futureSection}>
            <H2 style={styles.surfaceTitle}>Future Scaling 🚀</H2>
            <View style={styles.roadmapGrid}>
              <FutureBox
                icon="bulb-outline"
                label="Mentorship"
                color="#6366F1"
                onPress={() => handleNavigation('MentorHub')}
              />
              <FutureBox icon="briefcase-outline" label="Jobs" color="#10B981" />
              <FutureBox icon="heart-outline" label="Wellness" color="#EC4899" />
            </View>
          </View>

          {/* Support Links */}
          <View style={styles.supportSection}>
            <View style={styles.supportGrid}>
              <SupportLabel label="Help" icon="help-circle-outline" onPress={() => handleNavigation('Help')} />
              <SupportLabel label="Settings" icon="settings-outline" onPress={() => handleNavigation('Profile')} />
              <SupportLabel label="Terms" icon="document-text-outline" onPress={() => handleNavigation('Terms')} />
            </View>
          </View>

          {/* Learning Partner LMS Spotlight */}
          <View style={styles.lmsSpotlightSection}>
            <H2 style={styles.surfaceTitle}>Learning Partner</H2>
            <View style={styles.lmsSpotlightCard}>
              <Image
                source={{ uri: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800' }}
                style={styles.spotlightImage}
              />
              <View style={styles.lmsSpotlightOverlay}>
                <Body1 style={styles.spotlightTitle}>Intuit Learning LMS</Body1>
                <Caption style={styles.spotlightText}>Master mission-critical skills with our world-class learning management system.</Caption>
                <TouchableOpacity style={styles.lmsSpotlightBtn} onPress={() => handleNavigation('Soma')}>
                  <Caption style={styles.spotlightBtnText}>JOIN TO LEARN</Caption>
                </TouchableOpacity>
              </View>
            </View>
          </View>

          {/* Footer */}
          <View style={styles.footerBrand}>
            <Caption style={styles.footerCopy}>Youth Connect • Innovative Core v1.2</Caption>
          </View>

        </View>
      </Animated.ScrollView>

      {/* Video Modal */}
      <VideoModal
        isVisible={videoModalVisible}
        videoUrl={currentVideoUrl}
        onClose={() => setVideoModalVisible(false)}
      />

      {/* Mentor Profile Modal */}
      <MentorProfileModal
        visible={mentorModalVisible}
        mentor={selectedMentor}
        onClose={() => {
          setMentorModalVisible(false);
          setSelectedMentor(null);
        }}
      />
    </View>
  );
};

// Sub-components
const HubButton = ({ icon, label, color, onPress }) => (
  <TouchableOpacity style={styles.hubBtn} onPress={onPress}>
    <View style={[styles.hubIconBg, { backgroundColor: color + '12' }]}>
      <Ionicons name={icon} size={24} color={color} />
    </View>
    <Caption style={styles.hubLabel}>{label}</Caption>
  </TouchableOpacity>
);

// Compact grid card for the redesigned Discovery Hub
const HubCard = ({ icon, label, desc, color, onPress }) => (
  <TouchableOpacity
    style={[styles.hubCardCompact, { borderLeftColor: color }]}
    onPress={onPress}
    activeOpacity={0.8}
  >
    <View style={[styles.hubCardIconBg, { backgroundColor: color + '15' }]}>
      <Ionicons name={icon} size={22} color={color} />
    </View>
    <View style={styles.hubCardText}>
      <Caption style={[styles.hubCardLabel, { color: '#111827' }]}>{label}</Caption>
      <Caption style={styles.hubCardDesc} numberOfLines={1}>{desc}</Caption>
    </View>
    <Ionicons name="chevron-forward" size={14} color={color} />
  </TouchableOpacity>
);

const ActivityCard = ({ title, date, image }) => (
  <TouchableOpacity style={styles.activityCard}>
    <Image source={{ uri: image }} style={styles.activityImg} />
    <LinearGradient colors={['transparent', 'rgba(0,0,0,0.85)']} style={styles.activityOverlay}>
      <Caption style={styles.activityDate}>{date}</Caption>
      <Body1 style={styles.activityTitleText}>{title}</Body1>
    </LinearGradient>
  </TouchableOpacity>
);

const MentorCard = ({ name, type, image, color, socialLinks, onPress }) => (
  <TouchableOpacity style={styles.leaderBox} onPress={onPress}>
    <View style={[styles.leaderBorder, { borderColor: color || '#6366F1' }]}>
      <Image source={{ uri: getImageUrl(image) || 'https://i.pravatar.cc/150' }} style={styles.leaderImg} />
      <View style={[styles.leaderBadge, { backgroundColor: color || '#6366F1' }]}>
        <Ionicons name="star" size={8} color="#FFFFFF" />
      </View>
    </View>
    <Body2 style={styles.leaderNameTxt} numberOfLines={1}>{name}</Body2>
    <Caption style={styles.leaderPtsTxt} numberOfLines={1}>{type || 'Mentor'}</Caption>
    <View style={styles.socialRow}>
      {socialLinks?.instagram && (
        <View style={styles.socialIcon}>
          <Ionicons name="logo-instagram" size={14} color="#6366F1" />
        </View>
      )}
      {socialLinks?.twitter && (
        <View style={styles.socialIcon}>
          <Ionicons name="logo-twitter" size={14} color="#6366F1" />
        </View>
      )}
      {socialLinks?.linkedin && (
        <View style={styles.socialIcon}>
          <Ionicons name="logo-linkedin" size={14} color="#6366F1" />
        </View>
      )}
    </View>
  </TouchableOpacity>
);

const TrendChip = ({ name, count, color, onPress }) => (
  <TouchableOpacity style={styles.trendChip} onPress={onPress}>
    <View style={[styles.trendDot, { backgroundColor: color }]} />
    <Body2 style={styles.trendLabel}>{name}</Body2>
    <Caption style={[styles.trendValue, { color }]}>{count}</Caption>
  </TouchableOpacity>
);

const FutureBox = ({ icon, label, color, onPress }) => (
  <TouchableOpacity
    style={styles.futureBox}
    onPress={onPress}
    disabled={!onPress}
    activeOpacity={0.7}
  >
    <View style={[styles.futureIconBg, { backgroundColor: color + '15' }]}>
      <Ionicons name={icon} size={20} color={color} />
    </View>
    <Caption style={[styles.futureLabel, { color: color }]}>{label}</Caption>
  </TouchableOpacity>
);

const SupportLabel = ({ label, icon, onPress }) => (
  <TouchableOpacity style={styles.supportItem} onPress={onPress}>
    <Ionicons name={icon} size={18} color="#9CA3AF" />
    <Caption style={styles.supportTxt}>{label}</Caption>
  </TouchableOpacity>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  headerBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 0,
  },
  auraContainer: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
  },
  aura: {
    position: 'absolute',
    borderRadius: 150,
    opacity: 0.2,
  },
  aura1: {
    width: 300,
    height: 300,
    left: -100,
    top: -50,
    backgroundColor: '#C084FC',
  },
  aura2: {
    width: 250,
    height: 250,
    right: -50,
    top: -20,
    backgroundColor: '#818CF8',
  },
  aura3: {
    width: 200,
    height: 200,
    right: 30,
    bottom: -60,
    backgroundColor: '#4C1D95',
  },
  miniStickyHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    height: 120,
    elevation: 4,
  },
  miniHeaderContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    height: 50,
    marginTop: 25,
  },
  miniHeaderTitle: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '800',
  },
  miniStatsContainer: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
  },
  miniStatText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '700',
  },
  scrollView: {
    flex: 1,
    zIndex: 1,
  },
  scrollViewContent: {
    paddingBottom: 0,
  },
  heroHeader: {
    paddingHorizontal: 20,
    paddingBottom: 12,
    zIndex: 2,
  },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  avatarWrapper: {
    position: 'relative',
    marginRight: 15,
  },
  avatarBorder: {
    width: 54,
    height: 54,
    borderRadius: 27,
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.4)',
    padding: 2,
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 25,
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    borderRadius: 25,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusDot: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#10B981',
    borderWidth: 2,
    borderColor: '#6366f1',
  },
  greetingWrapper: {
    flex: 1,
  },
  greetLabel: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 14,
  },
  greetName: {
    color: '#FFFFFF',
    fontSize: 22,
    fontWeight: '700',
  },
  headerIcons: {
    flexDirection: 'row',
    gap: 12,
  },
  iconCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.25)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  notifDot: {
    position: 'absolute',
    top: 12,
    right: 12,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#EF4444',
    borderWidth: 1.5,
    borderColor: '#6366f1',
  },
  // Compact pill stats row
  statsPillRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 6,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  statPillCard: {
    flex: 1,
    alignItems: 'center',
    gap: 2,
  },
  statPillDivider: {
    width: 1,
    height: 28,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  statPillValue: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '800',
    lineHeight: 18,
  },
  statPillLabel: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 9,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  statPillEmoji: {
    fontSize: 13,
    lineHeight: 16,
  },
  contentSurface: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,


    paddingTop: 20,
    minHeight: SCREEN_HEIGHT - 100,
    zIndex: 5,
  },
  vibeCardModern: {
    marginHorizontal: 22,
    backgroundColor: '#F9FAFB',
    borderRadius: 14,
    marginBottom: 20,
    ...getShadow('sm'),
  },
  vibeInner: {
    padding: 20,
  },
  vibeHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  vibeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    gap: 4,
  },
  badgeText: {
    fontSize: 9,
    fontWeight: '900',
    color: '#6366F1',
  },
  fallbackBadge: {
    fontSize: 9,
    fontWeight: '600',
    color: '#9CA3AF',
    marginLeft: 2,
  },
  vibeContent: {
    fontSize: 14,
    lineHeight: 22,
    color: '#374151',
    marginBottom: 15,
  },
  vibeFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  vibeAuthor: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  authorIcon: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#E5E7EB',
  },
  authorName: {
    color: '#9CA3AF',
    fontWeight: '600',
  },
  watchBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: 'rgba(99, 102, 241, 0.1)',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
  },
  watchText: {
    color: '#6366F1',
    fontWeight: '800',
    fontSize: 11,
  },
  vibeVideoPreview: {
    marginTop: 10,
    marginBottom: 15,
    borderRadius: 12,
    overflow: 'hidden',
    height: 180,
    ...getShadow('sm'),
    backgroundColor: '#000',
  },
  vibeThumb: {
    width: '100%',
    height: '100%',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
  },
  vibePlayOverlay: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
  },
  vibePlayCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(99, 102, 241, 0.95)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: '#FFFFFF',
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
  },
  vibePlayTxt: {
    color: '#FFFFFF',
    fontWeight: '900',
    fontSize: 13,
    textShadowColor: 'rgba(0,0,0,0.6)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 4,
    letterSpacing: 0.3,
  },
  hubSection: {
    paddingHorizontal: 22,
    marginBottom: 32,
    marginTop: 0,
  },
  surfaceTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: '#111827',
    marginBottom: 6,
    letterSpacing: -0.4,
  },
  hubGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  hubBtn: {
    alignItems: 'center',
    width: '22%',
  },
  hubIconBg: {
    width: 54,
    height: 54,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  hubLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#6B7280',
  },
  hubSubtitle: {
    fontSize: 12,
    color: '#9CA3AF',
    fontWeight: '500',
  },
  hubGridCompact: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  hubCardCompact: {
    width: '48%',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 12,
    gap: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#6366F1',
    borderWidth: 1,
    borderColor: '#F1F5F9',
    elevation: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
  },
  hubCardIconBg: {
    width: 40,
    height: 40,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hubCardText: {
    flex: 1,
  },
  hubCardLabel: {
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 2,
  },
  hubCardDesc: {
    fontSize: 10,
    color: '#9CA3AF',
    fontWeight: '500',
  },
  progressSection: {
    paddingHorizontal: 22,
    marginBottom: 28,
  },
  progressCard: {
    borderRadius: 12,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  progressTextSide: {
    flex: 1,
    gap: 4,
  },
  progressTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  progressBadgePill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.15)',
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 8,
    gap: 4,
  },
  progressBadgeEmoji: {
    fontSize: 12,
  },
  progressBadgeLabel: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.4,
  },
  progressLvChip: {
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 8,
  },
  progressLvText: {
    fontSize: 10,
    fontWeight: '800',
  },
  progressTitle: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '800',
    lineHeight: 18,
  },
  progressLevel: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: 10,
    marginBottom: 6,
  },
  progressTrack: {
    height: 4,
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  rocketCircle: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: 'rgba(255,255,255,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
  },
  pointsCircleNum: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '900',
    lineHeight: 16,
  },
  pointsCircleLabel: {
    fontSize: 9,
    color: 'rgba(255,255,255,0.6)',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  leadersSection: {
    paddingHorizontal: 22,
    marginBottom: 32,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  mentorActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EEF2FF',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    gap: 4,
    borderWidth: 1,
    borderColor: '#E0E7FF',
  },
  mentorActionText: {
    color: '#6366F1',
    fontWeight: '800',
    fontSize: 12,
  },
  linkBlue: {
    color: '#6366F1',
    fontWeight: '700',
  },
  leadersScroll: {
    paddingLeft: 0,
  },
  leaderBox: {
    alignItems: 'center',
    marginRight: 20,
    width: 65,
  },
  leaderBorder: {
    width: 54,
    height: 54,
    borderRadius: 27,
    borderWidth: 2,
    padding: 2,
    marginBottom: 8,
    position: 'relative',
  },
  leaderImg: {
    width: '100%',
    height: '100%',
    borderRadius: 23,
    backgroundColor: '#F3F4F6',
  },
  leaderBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 16,
    height: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: '#FFFFFF',
  },
  leaderNameTxt: {
    fontSize: 11,
    fontWeight: '700',
    color: '#1F2937',
    textAlign: 'center',
  },
  leaderPtsTxt: {
    fontSize: 10,
    color: '#9CA3AF',
  },
  feedSection: {
    paddingHorizontal: 22,
    marginBottom: 32,
  },
  feedScroll: {
    paddingLeft: 0,
    marginTop: 5,
  },
  activityCard: {
    width: 200,
    height: 140,
    borderRadius: 12,
    overflow: 'hidden',
    marginRight: 15,
    backgroundColor: '#F3F4F6',
  },
  activityImg: {
    width: '100%',
    height: '100%',
  },
  activityOverlay: {
    ...StyleSheet.absoluteFillObject,
    padding: 15,
    justifyContent: 'flex-end',
  },
  activityDate: {
    color: '#E5E7EB',
    fontSize: 10,
    fontWeight: '700',
  },
  activityTitleText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
  trendsSection: {
    paddingHorizontal: 22,
    marginBottom: 32,
  },
  trendsContainer: {
    flexDirection: 'row',
    gap: 10,
  },
  trendChip: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    padding: 10,
    borderRadius: 8,
    gap: 8,
    justifyContent: 'center',
  },
  trendDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  trendLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#374151',
  },
  trendValue: {
    fontSize: 10,
    fontWeight: '800',
  },
  spotlightSection: {
    paddingHorizontal: 22,
    marginBottom: 32,
  },
  spotlightCard: {
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#F3F4F6',
    ...getShadow('sm'),
  },
  spotlightImage: {
    width: '100%',
    height: 120,
  },
  spotlightOverlay: {
    padding: 18,
  },
  spotlightTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#111827',
  },
  eventsSection: {
    paddingHorizontal: 0,
    marginBottom: 5,
  },
  spotlightText: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 5,
  },
  spotlightBtn: {
    marginTop: 15,
    backgroundColor: '#6366F1',
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
  },
  spotlightBtnText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '900',
    letterSpacing: 1,
  },
  futureSection: {
    paddingHorizontal: 22,
    marginBottom: 32,
  },
  roadmapGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  futureBox: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    padding: 15,
    borderRadius: 12,
  },
  futureIconBg: {
    width: 44,
    height: 44,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  futureLabel: {
    fontSize: 11,
    fontWeight: '800',
  },
  lmsSpotlightSection: {
    paddingHorizontal: 22,
    marginBottom: 32,
  },
  lmsSpotlightCard: {
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#F1F5F9',
    ...getShadow('sm'),
  },
  lmsSpotlightOverlay: {
    padding: 18,
    backgroundColor: 'rgba(16, 185, 129, 0.03)',
  },
  lmsSpotlightBtn: {
    marginTop: 15,
    backgroundColor: '#10B981',
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
  },
  supportSection: {
    paddingHorizontal: 22,
    marginBottom: 20,
  },
  supportGrid: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 20,
  },
  supportItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  supportTxt: {
    color: '#9CA3AF',
    fontWeight: '700',
    fontSize: 12,
  },
  footerBrand: {
    alignItems: 'center',
    paddingBottom: 8,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: '#F3F4F6',
    marginHorizontal: 22,
  },
  footerCopy: {
    color: '#D1D5DB',
    fontSize: 10,
  },
  socialRow: {
    flexDirection: 'row',
    marginTop: 8,
    gap: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  socialIcon: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  notifBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: '#EF4444',
    minWidth: 14,
    height: 14,
    borderRadius: 7,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 3,
    borderWidth: 1.5,
    borderColor: 'rgba(99, 102, 241, 1)',
  },
  notifBadgeText: {
    color: '#FFFFFF',
    fontSize: 8,
    fontWeight: '900',
  },
  sectionLoader: {
    width: SCREEN_WIDTH - 44,
    height: 100,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  loaderTxt: {
    color: '#6366F1',
    fontWeight: '700',
    fontSize: 12,
  },
  emptyLeaders: {
    width: SCREEN_WIDTH - 44,
    height: 100,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F1F5F9',
    borderStyle: 'dashed',
  },
  emptyLeadersTxt: {
    color: '#94A3B8',
    fontSize: 13,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default Dashboard;
